import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Button,
  Drawer,
  HStack,
  Heading,
  IconButton,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { HiX } from 'react-icons/hi';

import { MessageList } from '#src/components/message-list';
import { toaster } from '#src/components/ui/toaster';
import { CREATE_MESSAGE } from '#src/graphql/mutations/create-message';
import { GET_MESSAGES } from '#src/graphql/queries/messages';
import { useCurrentUser } from '#src/providers/user-provider';

interface DocumentMessagesDrawerProps {
  documentId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DocumentMessagesDrawer({
  documentId,
  open,
  onOpenChange,
}: DocumentMessagesDrawerProps) {
  const user = useCurrentUser();
  const [messageText, setMessageText] = useState('');
  const [parentTargetId, setParentTargetId] = useState<string | null>(null);

  const [createMessage, { loading: isSendingMessage }] =
    useMutation(CREATE_MESSAGE);

  const {
    data: messagesData,
    loading: isLoadingMessages,
    error: messagesError,
    refetch: refetchMessages,
  } = useQuery(GET_MESSAGES, {
    variables: {
      input: {
        contextFilters: [{ name: 'documentId', value: documentId }],
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: !open,
  });

  const filteredMessages = [...(messagesData?.messages || [])].sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [filteredMessages]);

  const handleSendMessage = async () => {
    const value = messageText.trim();
    if (!value) return;
    try {
      await createMessage({
        variables: {
          input: {
            value,
            parentMessageId: parentTargetId ?? undefined,
            contexts: [{ name: 'documentId', value: documentId }],
          },
        },
      });
      setMessageText('');
      await refetchMessages();
      toaster.create({
        title: 'Message sent',
        description: 'Your message was created successfully',
        type: 'success',
      });
    } catch (error) {
      toaster.create({
        title: 'Failed to send message',
        description:
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred',
        type: 'error',
      });
    }
  };

  const handleClose = () => {
    setMessageText('');
    setParentTargetId(null);
    onOpenChange(false);
  };

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
      placement="end"
      size="md"
    >
      <Drawer.Backdrop />
      <Drawer.Positioner>
        <Drawer.Content display="flex" flexDirection="column">
          <Drawer.Header
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Heading size="md" color="blue.600">
              Document messages
            </Heading>
            <IconButton
              aria-label="Close document messages"
              variant="ghost"
              size="sm"
              onClick={handleClose}
            >
              <HiX />
            </IconButton>
          </Drawer.Header>
          <Drawer.Body
            display="flex"
            flexDirection="column"
            gap={4}
            overflow="hidden"
            flex={1}
            minHeight={0}
          >
            {!user.email ? (
              <Alert.Root status="info">
                <Alert.Indicator />
                <Alert.Title>
                  Please set your user email in Settings to view messages.
                </Alert.Title>
              </Alert.Root>
            ) : (
              <MessageList
                messages={filteredMessages}
                loading={isLoadingMessages}
                error={messagesError ?? undefined}
                emptyMessage="No messages for this document yet."
                parentTargetId={parentTargetId}
                onSetParentTargetId={setParentTargetId}
                messagesEndRef={messagesEndRef}
              />
            )}

            {user.email && (
              <VStack gap={2} align="stretch" flexShrink={0}>
                {parentTargetId && (
                  <HStack gap={2} align="center" fontSize="xs" color="fg.muted">
                    <Text>Replying to message</Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => setParentTargetId(null)}
                    >
                      Cancel
                    </Button>
                  </HStack>
                )}
                <HStack gap={2} align="flex-end" width="100%">
                  <Textarea
                    flex={1}
                    placeholder="Type a message..."
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    size="sm"
                    fontSize="16px"
                    rows={2}
                    resize="none"
                    minH="40px"
                    maxH="120px"
                  />
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleSendMessage}
                    disabled={!messageText.trim() || isSendingMessage}
                    loading={isSendingMessage}
                    loadingText="Sending..."
                  >
                    Send
                  </Button>
                </HStack>
              </VStack>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  );
}
